import React, { useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useSortedPaginated, SortableTh, Pagination, useFilters, FilterRow } from '../components/TableControls';
import { formatDate } from '../utils/dateUtils';
import useAutoSelectSingle from '../hooks/useAutoSelectSingle';
import InvalidateWarningModal from '../components/InvalidateWarningModal';

export default function Transfers() {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [form, setForm] = useState({ from_store_id: '', to_store_id: '', item_id: '', quantity: '', date: new Date().toISOString().slice(0, 10) });
  const [msg, setMsg] = useState('');
  const [msgErr, setMsgErr] = useState(false);
  const [editTxn, setEditTxn] = useState(null);
  const [invalidate, setInvalidate] = useState(null);
  const { filters, setFilter, filtered } = useFilters(transactions, ['store_name', 'to_store_name', 'item_name', 'user_name']);
  const { paginated, sortKey, sortDir, setSort, page, setPage, perPage, setPerPage, total } = useSortedPaginated(filtered, 'date', 'desc');

  const loadTxns = () => {
    Promise.all([
      api.get('/transactions', { params: { transaction_type: 'transfer_out' } }),
      api.get('/transactions', { params: { transaction_type: 'transfer_in' } }),
    ]).then(([outRes, inRes]) => {
      const inMap = {};
      inRes.data.forEach(t => { inMap[t.id] = t.store_name; });
      const enriched = outRes.data.map(t => ({
        ...t,
        to_store_name: inMap[t.reference_id] || '-',
      }));
      setTransactions(enriched);
    });
  };

  useEffect(() => {
    api.get('/stores').then(r => setStores(r.data));
    api.get('/items').then(r => setItems(r.data));
    loadTxns();
  }, []);

  // Transfers need two distinct stores, so only the item is safe to auto-select.
  useAutoSelectSingle(items, form.item_id, (id) => setForm(f => ({ ...f, item_id: id })));

  const submit = (e) => {
    e.preventDefault();
    if (form.from_store_id === form.to_store_id) {
      setMsg('No se puede transferir a la misma tienda'); setMsgErr(true);
      return;
    }
    doSubmit(false);
  };

  const doSubmit = async (force = false) => {
    setMsg(''); setMsgErr(false);
    try {
      await api.post(`/transactions/transfer${force ? '?force=true' : ''}`, {
        from_store_id: parseInt(form.from_store_id),
        to_store_id: parseInt(form.to_store_id),
        item_id: parseInt(form.item_id),
        quantity: parseInt(form.quantity, 10),
        date: form.date,
      });
      setMsg('¡Transferencia registrada!');
      setForm({ ...form, item_id: '', quantity: '' });
      loadTxns();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code === 'WILL_INVALIDATE') {
        setInvalidate({ status: d.status, run: () => doSubmit(true) });
        return;
      }
      setMsg(typeof d === 'string' ? d : 'Error'); setMsgErr(true);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`¿Eliminar esta transferencia de ${t.quantity} x ${t.item_name}? Ambos lados de la transferencia serán eliminados.`)) return;
    doDelete(t, false);
  };

  const doDelete = async (t, force) => {
    setMsg(''); setMsgErr(false);
    try {
      const res = await api.delete(`/transactions/${t.id}${force ? '?force=true' : ''}`);
      setMsg(res.data.message);
      loadTxns();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code === 'WILL_INVALIDATE') {
        setInvalidate({ status: d.status, run: () => doDelete(t, true) });
        return;
      }
      setMsg(typeof d === 'string' ? d : 'Error'); setMsgErr(true);
    }
  };

  const openEdit = (t) => setEditTxn({ id: t.id, quantity: t.quantity, date: t.date, label: `${t.item_name}: ${t.store_name} → ${t.to_store_name}` });

  const saveEdit = (e) => { e.preventDefault(); doSaveEdit(false); };

  const doSaveEdit = async (force) => {
    setMsg(''); setMsgErr(false);
    try {
      await api.put(`/transactions/${editTxn.id}${force ? '?force=true' : ''}`, {
        quantity: parseInt(editTxn.quantity, 10),
        date: editTxn.date,
      });
      setMsg('¡Transferencia actualizada!');
      setEditTxn(null);
      loadTxns();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code === 'WILL_INVALIDATE') {
        setInvalidate({ status: d.status, run: () => doSaveEdit(true) });
        return;
      }
      setMsg(typeof d === 'string' ? d : 'Error'); setMsgErr(true);
    }
  };

  return (
    <div>
      <div className="page-header"><h1>Transferencias</h1></div>

      <div className="card">
        <h3 style={{ marginBottom: 12 }}>Registrar Transferencia</h3>
        {msg && <div className={`alert ${msgErr ? 'alert-danger' : 'alert-success'}`}>{msg}</div>}
        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            <div className="form-group">
              <label>Tienda Origen</label>
              <select value={form.from_store_id} onChange={e => setForm({...form, from_store_id: e.target.value})} required>
                <option value="">Seleccionar tienda</option>
                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Tienda Destino</label>
              <select value={form.to_store_id} onChange={e => setForm({...form, to_store_id: e.target.value})} required>
                <option value="">Seleccionar tienda</option>
                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Artículo</label>
              <select value={form.item_id} onChange={e => setForm({...form, item_id: e.target.value})} required>
                <option value="">Seleccionar artículo</option>
                {items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Cantidad</label>
              <input type="number" step="1" min="1" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Fecha</label>
              <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} required />
            </div>
          </div>
          <button type="submit" className="btn btn-primary">Registrar Transferencia</button>
        </form>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: 12 }}>Transferencias Recientes</h3>
        <Pagination page={page} perPage={perPage} total={total} onPage={setPage} onPerPage={setPerPage} />
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <SortableTh colKey="date" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Fecha</SortableTh>
                <SortableTh colKey="store_name" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Origen</SortableTh>
                <SortableTh colKey="to_store_name" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Destino</SortableTh>
                <SortableTh colKey="item_name" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Artículo</SortableTh>
                <SortableTh colKey="quantity" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Cant.</SortableTh>
                <SortableTh colKey="user_name" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Por</SortableTh>
                {(user?.role === 'admin' || user?.role === 'manager') && <th>Acciones</th>}
              </tr>
              <FilterRow
                columns={[null, 'store_name', 'to_store_name', 'item_name', null, 'user_name', ...(user?.role === 'admin' || user?.role === 'manager' ? [null] : [])]}
                data={transactions}
                filters={filters}
                setFilter={setFilter}
              />
            </thead>
            <tbody>
              {paginated.map(t => (
                <tr key={t.id}>
                  <td>{formatDate(t.date)}</td><td>{t.store_name}</td><td>{t.to_store_name}</td><td>{t.item_name}</td>
                  <td>{t.quantity}</td><td>{t.user_name}</td>
                  {(user?.role === 'admin' || user?.role === 'manager') && (
                    <td>
                      <button className="btn btn-sm btn-secondary" onClick={() => openEdit(t)}>Editar</button>
                      {' '}
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(t)}>Eliminar</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} perPage={perPage} total={total} onPage={setPage} onPerPage={setPerPage} />
      </div>

      {editTxn && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Editar Transferencia</h2>
            <p style={{ color: 'var(--text-2)', marginBottom: 12 }}>{editTxn.label}</p>
            <form onSubmit={saveEdit}>
              <div className="form-group">
                <label>Cantidad</label>
                <input type="number" step="1" min="1" value={editTxn.quantity} onChange={e => setEditTxn({...editTxn, quantity: e.target.value})} required autoFocus />
              </div>
              <div className="form-group">
                <label>Fecha</label>
                <input type="date" value={editTxn.date} onChange={e => setEditTxn({...editTxn, date: e.target.value})} required />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setEditTxn(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <InvalidateWarningModal
        data={invalidate}
        onCancel={() => setInvalidate(null)}
        onConfirm={() => { const r = invalidate.run; setInvalidate(null); r(); }}
      />
    </div>
  );
}
